import { useEffect, useState } from "react";
import { useCompany } from "@/contexts/CompanyContext";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  TrendingUp,
  Target,
  AlertCircle,
  CheckCircle2,
  BarChart3,
  PieChart,
  Activity,
  Calendar,
  RefreshCw,
} from "lucide-react";

export default function CategorizationAnalytics() {
  const { selectedCompanyId } = useCompany();
  const [dateRange, setDateRange] = useState<{ from?: number; to?: number }>(
    {}
  );

  // Get company details
  const { data: companies } = trpc.company.list.useQuery();
  const selectedCompany = companies?.find(c => c.id === selectedCompanyId);

  const {
    data: analytics,
    isLoading,
    refetch,
  } = trpc.analytics.getCategorizationAnalytics.useQuery(
    {
      companyId: selectedCompanyId || 0,
      dateFrom: dateRange.from,
      dateTo: dateRange.to,
    },
    {
      enabled: !!selectedCompanyId,
    }
  );

  if (!selectedCompanyId) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <Card>
            <CardHeader>
              <CardTitle>No Company Selected</CardTitle>
              <CardDescription>
                Please select a company to view categorization analytics.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Loading analytics...</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!analytics) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <Card>
            <CardHeader>
              <CardTitle>No Data Available</CardTitle>
              <CardDescription>
                No transaction data found for analysis.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Destructure with safe defaults
  const {
    overview,
    confidenceDistribution = { high: 0, medium: 0, low: 0, none: 0 },
    categoryBreakdown = [],
    ruleEffectiveness = [],
    matchTypeDistribution = { rule: 0, default: 0, override: 0, none: 0 },
    uncategorizedPatterns = [],
    trends = [],
  } = analytics;

  // Guard against missing overview data
  if (!overview) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <Card>
            <CardHeader>
              <CardTitle>No Analytics Data</CardTitle>
              <CardDescription>
                Analytics data is not yet available. Please upload some
                transactions first.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Categorization Analytics</h1>
            <p className="text-muted-foreground mt-1">
              Analyze rule effectiveness and categorization accuracy
              {selectedCompany ? ` for ${selectedCompany.name}` : ""}
            </p>
          </div>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Overview KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {overview.totalTransactions.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Categorization Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[var(--success)]">
                {overview.categorizationRate.toFixed(1)}%
              </div>
              <Progress value={overview.categorizationRate} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Active Rules
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{overview.activeRules}</div>
              <p className="text-sm text-muted-foreground mt-1">
                of {overview.totalRules} total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Uncategorized
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[var(--warning)]">
                {overview.uncategorizedTransactions.toLocaleString()}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {overview.totalTransactions > 0
                  ? (
                      (overview.uncategorizedTransactions /
                        overview.totalTransactions) *
                      100
                    ).toFixed(1)
                  : 0}
                % of total
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="confidence" className="space-y-4">
          <TabsList>
            <TabsTrigger value="confidence">
              <Target className="h-4 w-4 mr-2" />
              Confidence Scores
            </TabsTrigger>
            <TabsTrigger value="rules">
              <BarChart3 className="h-4 w-4 mr-2" />
              Rule Effectiveness
            </TabsTrigger>
            <TabsTrigger value="categories">
              <PieChart className="h-4 w-4 mr-2" />
              Categories
            </TabsTrigger>
            <TabsTrigger value="patterns">
              <AlertCircle className="h-4 w-4 mr-2" />
              Uncategorized Patterns
            </TabsTrigger>
            <TabsTrigger value="trends">
              <Activity className="h-4 w-4 mr-2" />
              Trends
            </TabsTrigger>
          </TabsList>

          {/* Confidence Distribution Tab */}
          <TabsContent value="confidence" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Confidence Score Distribution</CardTitle>
                <CardDescription>
                  How confident the system is about categorization decisions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-[var(--success)]" />
                      <span className="font-medium">
                        High Confidence (≥70%)
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold">
                        {confidenceDistribution.high}
                      </span>
                      <Badge
                        variant="outline"
                        className="bg-[var(--success)]/10 text-[var(--success)] border-[var(--success)]/20"
                      >
                        {overview.totalTransactions > 0
                          ? (
                              (confidenceDistribution.high /
                                overview.totalTransactions) *
                              100
                            ).toFixed(1)
                          : 0}
                        %
                      </Badge>
                    </div>
                  </div>
                  <Progress
                    value={
                      (confidenceDistribution.high /
                        overview.totalTransactions) *
                      100
                    }
                    className="bg-[var(--success)]/10"
                  />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-[var(--warning)]" />
                      <span className="font-medium">
                        Medium Confidence (50-69%)
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold">
                        {confidenceDistribution.medium}
                      </span>
                      <Badge
                        variant="outline"
                        className="bg-[var(--warning)]/10 text-[var(--warning)] border-[var(--warning)]/20"
                      >
                        {overview.totalTransactions > 0
                          ? (
                              (confidenceDistribution.medium /
                                overview.totalTransactions) *
                              100
                            ).toFixed(1)
                          : 0}
                        %
                      </Badge>
                    </div>
                  </div>
                  <Progress
                    value={
                      (confidenceDistribution.medium /
                        overview.totalTransactions) *
                      100
                    }
                    className="bg-[var(--warning)]/10"
                  />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-destructive" />
                      <span className="font-medium">
                        Low Confidence (&lt;50%)
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold">
                        {confidenceDistribution.low}
                      </span>
                      <Badge
                        variant="outline"
                        className="bg-destructive/10 text-destructive border-destructive/20"
                      >
                        {overview.totalTransactions > 0
                          ? (
                              (confidenceDistribution.low /
                                overview.totalTransactions) *
                              100
                            ).toFixed(1)
                          : 0}
                        %
                      </Badge>
                    </div>
                  </div>
                  <Progress
                    value={
                      (confidenceDistribution.low /
                        overview.totalTransactions) *
                      100
                    }
                    className="bg-destructive/10"
                  />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium">
                        No Confidence (Uncategorized)
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold">
                        {confidenceDistribution.none}
                      </span>
                      <Badge
                        variant="outline"
                        className="bg-muted/20 text-muted-foreground border-muted"
                      >
                        {overview.totalTransactions > 0
                          ? (
                              (confidenceDistribution.none /
                                overview.totalTransactions) *
                              100
                            ).toFixed(1)
                          : 0}
                        %
                      </Badge>
                    </div>
                  </div>
                  <Progress
                    value={
                      (confidenceDistribution.none /
                        overview.totalTransactions) *
                      100
                    }
                    className="bg-muted/20"
                  />
                </div>

                <div className="mt-6 p-4 bg-[var(--info)]/10 rounded-lg border border-[var(--info)]/20">
                  <h4 className="font-semibold text-[var(--info)] mb-2">
                    Match Type Distribution
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-[var(--info)]">
                        🎯 Custom Rules
                      </div>
                      <div className="text-2xl font-bold text-foreground">
                        {matchTypeDistribution.rule}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-[var(--info)]">
                        🔍 Swiss Merchants
                      </div>
                      <div className="text-2xl font-bold text-foreground">
                        {matchTypeDistribution.default}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-[var(--info)]">
                        ✏️ Manual Overrides
                      </div>
                      <div className="text-2xl font-bold text-foreground">
                        {matchTypeDistribution.override}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-[var(--info)]">
                        ❌ No Match
                      </div>
                      <div className="text-2xl font-bold text-foreground">
                        {matchTypeDistribution.none}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rule Effectiveness Tab */}
          <TabsContent value="rules" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Rule Effectiveness</CardTitle>
                <CardDescription>
                  Performance metrics for each categorization rule
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rule Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead className="text-right">Matches</TableHead>
                      <TableHead className="text-right">Match Rate</TableHead>
                      <TableHead className="text-right">
                        Avg Confidence
                      </TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ruleEffectiveness.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          className="text-center text-muted-foreground py-8"
                        >
                          No rules found. Create rules to start automatic
                          categorization.
                        </TableCell>
                      </TableRow>
                    ) : (
                      ruleEffectiveness.map(rule => (
                        <TableRow key={rule.ruleId}>
                          <TableCell className="font-medium">
                            {rule.ruleName}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{rule.ruleType}</Badge>
                          </TableCell>
                          <TableCell>{rule.category}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{rule.priority}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {rule.matchCount}
                          </TableCell>
                          <TableCell className="text-right">
                            {rule.matchRate}%
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge
                              variant="outline"
                              className={
                                rule.avgConfidence >= 70
                                  ? "bg-[var(--success)]/10 text-[var(--success)] border-[var(--success)]/20"
                                  : rule.avgConfidence >= 50
                                    ? "bg-[var(--warning)]/10 text-[var(--warning)] border-[var(--warning)]/20"
                                    : "bg-destructive/10 text-destructive border-destructive/20"
                              }
                            >
                              {rule.avgConfidence}%
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {rule.isActive ? (
                              <Badge
                                variant="outline"
                                className="bg-[var(--success)]/10 text-[var(--success)] border-[var(--success)]/20"
                              >
                                Active
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="bg-muted/20 text-muted-foreground border-muted"
                              >
                                Inactive
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Category Breakdown Tab */}
          <TabsContent value="categories" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Category Breakdown</CardTitle>
                <CardDescription>
                  Transaction distribution across categories
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Transactions</TableHead>
                      <TableHead className="text-right">Percentage</TableHead>
                      <TableHead className="text-right">Total Amount</TableHead>
                      <TableHead className="text-right">
                        Avg Confidence
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categoryBreakdown.map(cat => (
                      <TableRow key={cat.category}>
                        <TableCell className="font-medium">
                          {cat.category}
                        </TableCell>
                        <TableCell className="text-right">
                          {cat.count}
                        </TableCell>
                        <TableCell className="text-right">
                          {cat.percentage.toFixed(1)}%
                        </TableCell>
                        <TableCell className="text-right">
                          CHF {(cat.totalAmount / 100).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge
                            variant="outline"
                            className={
                              cat.avgConfidence >= 70
                                ? "bg-[var(--success)]/10 text-[var(--success)] border-[var(--success)]/20"
                                : cat.avgConfidence >= 50
                                  ? "bg-[var(--warning)]/10 text-[var(--warning)] border-[var(--warning)]/20"
                                  : cat.avgConfidence > 0
                                    ? "bg-destructive/10 text-destructive border-destructive/20"
                                    : "bg-muted/20 text-muted-foreground border-muted"
                            }
                          >
                            {cat.avgConfidence}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Uncategorized Patterns Tab */}
          <TabsContent value="patterns" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Uncategorized Transaction Patterns</CardTitle>
                <CardDescription>
                  Common patterns in uncategorized transactions - create rules
                  to automate these
                </CardDescription>
              </CardHeader>
              <CardContent>
                {uncategorizedPatterns.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-12 w-12 text-[var(--success)] mx-auto mb-4" />
                    <p className="text-lg font-semibold">
                      All transactions categorized!
                    </p>
                    <p className="text-muted-foreground">
                      No uncategorized patterns found.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {uncategorizedPatterns.map((pattern, idx) => (
                      <div key={idx} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-semibold text-lg">
                            {pattern.pattern}
                          </div>
                          <Badge variant="destructive">
                            {pattern.count} transactions
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p className="font-medium">Examples:</p>
                          {pattern.examples.map((example, i) => (
                            <p key={i} className="pl-4">
                              • {example}
                            </p>
                          ))}
                        </div>
                        <Button size="sm" variant="outline" className="mt-3">
                          Create Rule for This Pattern
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Trends</CardTitle>
                <CardDescription>
                  Categorization performance over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">
                        Total Transactions
                      </TableHead>
                      <TableHead className="text-right">Categorized</TableHead>
                      <TableHead className="text-right">
                        Categorization Rate
                      </TableHead>
                      <TableHead className="text-right">
                        Avg Confidence
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trends.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-muted-foreground py-8"
                        >
                          No trend data available yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      trends.map(trend => (
                        <TableRow key={trend.month}>
                          <TableCell className="font-medium">
                            <Calendar className="h-4 w-4 inline mr-2" />
                            {trend.month}
                          </TableCell>
                          <TableCell className="text-right">
                            {trend.total}
                          </TableCell>
                          <TableCell className="text-right">
                            {trend.categorized}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Progress
                                value={trend.categorizationRate}
                                className="w-20"
                              />
                              <span>
                                {trend.categorizationRate.toFixed(1)}%
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge
                              variant="outline"
                              className={
                                trend.avgConfidence >= 70
                                  ? "bg-[var(--success)]/10 text-[var(--success)] border-[var(--success)]/20"
                                  : trend.avgConfidence >= 50
                                    ? "bg-[var(--warning)]/10 text-[var(--warning)] border-[var(--warning)]/20"
                                    : "bg-destructive/10 text-destructive border-destructive/20"
                              }
                            >
                              {trend.avgConfidence}%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
